<template>
  <div class="darknet-importer">
    <div class="darknet-importer__group app-start-schema-card">
      <h2 class="darknet-importer__title">{{ sourceLabel }}</h2>
      <hr class="darknet-importer__divider">
      <div class="uploader">
        <label class="uploader__label">{{ sourceUploadField.label || 'Select Files/Folder' }}</label>
        <div
          class="drop-zone"
          :class="{ _highlight: dropHighlighted }"
          @dragenter.prevent="handleDragEnter"
          @dragover.prevent=""
          @dragleave="handleDragLeave"
          @drop.stop.prevent="handleFileDrop"
        >
          <input
            v-if="usesFolderInput"
            type="file"
            multiple
            webkitdirectory
            directory
            :accept="uploadAccept || undefined"
            @change="handleSelectFileInput"
          >
          <input
            v-else
            type="file"
            multiple
            :accept="uploadAccept || undefined"
            @change="handleSelectFileInput"
          >
          <span class="drop-zone__hint">{{ dropZoneHint }}</span>
        </div>
        <div class="uploader__row">
          <label v-if="showsSelectionToggle" class="uploader__toggle">
            <input v-model="selectFolder" type="checkbox">
            Select folder
          </label>
          <div class="uploader__status">{{ statusMessage }}</div>
        </div>
        <hr>
        <div v-if="fileObjs.length > 0 && fileObjs.length <= 20" class="file-list">
          <div v-for="(file, i) in fileObjs" :key="`file-${i}`" class="file-list__row">
            <IconButton
              :id="`file-${i}-remove-icon`"
              :icon="'cancel_filled'"
              :width="16"
              :height="16"
              @click="removeFile(i)"
            />
            <span>{{ file.name }}</span>
          </div>
        </div>
        <div v-else-if="fileObjs.length > 20" class="file-list">
          <span>{{ fileObjs.length }} files selected</span>
        </div>
      </div>

      <v-checkbox
        v-model="shouldCreateImageGroups"
        density="compact"
        hide-details="true"
        :label="imageGroupsLabel"
      />
    </div>

    <SchemaGroup
      ref="schemaGroupRef"
      v-model:params="destinationParams"
      :fields="destinationFields"
      :defaultValues="defaultValues"
      :cardTitle="destinationLabel"
    />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { encodeID } from '@/assets/js/utils.js';
import IconButton from '@/components/IconButton.vue';
import SchemaGroup from '@/components/Schema/SchemaGroup.vue';
import { useImporterStore } from '@/stores/useImporterStore.js';
import { appSchema } from './schema.js';

const props = defineProps({
  appDetails: {
    type: Object,
    default: () => ({}),
  },
  defaultValues: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['completed']);

const $toast = useToast();
const router = useRouter();
const store = useStore();
const importerStore = useImporterStore();
const dataConnect = new DatastoreConnect();

const schemaGroupRef = ref(null);
const selectFolder = ref(true);
const dropHighlighted = ref(false);
const dragCounter = ref(0);
const fileObjs = ref([]);
const shouldCreateImageGroups = ref(Boolean(appSchema.find((field) => field.name === 'source')?.children?.find((field) => field.type === 'bool' && field.name === 'image_groups')?.default));
const imageGroupsToCreate = ref([]);
const destinationParams = ref(JSON.parse(JSON.stringify(props.defaultValues?.destination || {})));
const createdDataset = ref(null);

const sourceGroup = computed(() => appSchema.find((field) => field.name === 'source') || {});
const destinationGroup = computed(() => appSchema.find((field) => field.name === 'destination') || {});
const sourceLabel = computed(() => sourceGroup.value?.label || 'Source');
const destinationLabel = computed(() => destinationGroup.value?.label || 'Destination');
const sourceFields = computed(() => sourceGroup.value?.children || []);
const destinationFields = computed(() => destinationGroup.value?.children || []);
const sourceUploadField = computed(() => sourceFields.value.find((field) => field.type === 'upload') || {});
const imageGroupsField = computed(() => sourceFields.value.find((field) => field.type === 'bool' && field.name === 'image_groups') || {});

const uploadAccept = computed(() => {
  const filter = sourceUploadField.value?.files_filter;
  return Array.isArray(filter) && filter.length > 0 ? filter.join(',') : null;
});

const allowedExtensions = computed(() => {
  const filter = sourceUploadField.value?.files_filter;
  if (!Array.isArray(filter) || filter.length === 0) {
    return [];
  }

  return filter
    .map((extension) => String(extension).trim().replace(/^\./, '').toLowerCase())
    .filter((extension) => extension.length > 0);
});

const selectionMode = computed(() => {
  const mode = String(sourceUploadField.value?.selection_mode || 'mixed').toLowerCase();
  return ['mixed', 'files', 'folder'].includes(mode) ? mode : 'mixed';
});

const allowsFileSelection = computed(() => selectionMode.value === 'mixed' || selectionMode.value === 'files');
const allowsFolderSelection = computed(() => selectionMode.value === 'mixed' || selectionMode.value === 'folder');
const showsSelectionToggle = computed(() => selectionMode.value === 'mixed');
const usesFolderInput = computed(() => selectionMode.value === 'folder' || (selectionMode.value === 'mixed' && selectFolder.value));
const dropZoneHint = computed(() => {
  if (selectionMode.value === 'files') {
    return 'Drag and drop files here or use the file picker';
  }
  if (selectionMode.value === 'folder') {
    return 'Drag and drop a folder here or use the folder picker';
  }
  return 'Drag and drop files or a folder here';
});
const statusMessage = computed(() => {
  if (fileObjs.value.length > 0) {
    return '';
  }
  return usesFolderInput.value ? 'Select folder' : 'Select files';
});
const imageGroupsLabel = computed(() => imageGroupsField.value?.label || 'Create image groups from folder names');

function handleSelectFileInput(event) {
  const files = Array.from(event.target.files || []).filter((file) => isAllowedFile(file.name));
  // Top-level subfolders of the selected folder become image groups.
  const groups = new Set();
  files.forEach((file) => {
    const parts = String(file.webkitRelativePath || '').split('/');
    if (parts.length > 2) {
      groups.add(parts[1]);
    }
  });
  imageGroupsToCreate.value = Array.from(groups);
  fileObjs.value = files;
}

function removeFile(index) {
  fileObjs.value.splice(index, 1);
}

function handleDragEnter() {
  dragCounter.value += 1;
  dropHighlighted.value = true;
}

function handleDragLeave() {
  dragCounter.value -= 1;
  if (dragCounter.value <= 0) {
    dropHighlighted.value = false;
    dragCounter.value = 0;
  }
}

function isAllowedFile(fileName) {
  if (allowedExtensions.value.length === 0) {
    return true;
  }
  const extension = String(fileName || '').split('.').pop()?.toLowerCase();
  return allowedExtensions.value.includes(extension);
}

async function collectDroppedDirectoryFiles(directoryHandle, relativePath = directoryHandle.name) {
  const files = [];

  for await (const [name, handle] of directoryHandle) {
    if (handle.kind === 'directory') {
      files.push(...await collectDroppedDirectoryFiles(handle, `${relativePath}/${name}`));
    } else {
      const file = await handle.getFile();
      if (isAllowedFile(file.name)) {
        file.relativePath = `${relativePath}/${name}`;
        files.push(file);
      }
    }
  }

  return files;
}

async function listTopLevelDirectories(directoryHandle) {
  const directoryNames = [];

  for await (const [name, handle] of directoryHandle) {
    if (handle.kind === 'directory') {
      directoryNames.push(name);
    }
  }

  return directoryNames;
}

async function collectDroppedEntry(entry) {
  if (!entry) {
    return { files: [], imageGroups: [] };
  }

  if (entry.kind === 'directory') {
    if (!allowsFolderSelection.value) {
      return { files: [], imageGroups: [] };
    }

    const [imageGroups, files] = await Promise.all([
      listTopLevelDirectories(entry),
      collectDroppedDirectoryFiles(entry),
    ]);

    return { files, imageGroups };
  }

  if (!allowsFileSelection.value) {
    return { files: [], imageGroups: [] };
  }

  const file = await entry.getFile();
  return {
    files: isAllowedFile(file.name) ? [file] : [],
    imageGroups: [],
  };
}

async function handleFileDrop(event) {
  dragCounter.value = 0;
  dropHighlighted.value = false;

  const items = Array.from(event.dataTransfer?.items || []);
  if (items.length > 0 && typeof items[0].getAsFileSystemHandle === 'function') {
    const entries = await Promise.all(
      items
        .filter((item) => item.kind === 'file')
        .map((item) => item.getAsFileSystemHandle()),
    );

    const droppedEntries = await Promise.all(entries.map((entry) => collectDroppedEntry(entry)));
    imageGroupsToCreate.value.push(...droppedEntries.flatMap((entry) => entry.imageGroups));
    fileObjs.value = droppedEntries.flatMap((entry) => entry.files);
    return;
  }

  if (!allowsFileSelection.value) {
    fileObjs.value = [];
    return;
  }

  fileObjs.value = Array.from(event.dataTransfer?.files || []).filter((file) => isAllowedFile(file.name));
}

function buildImportParams() {
  const datasetInfo = getExistingDatasetInfo();
  const newDataset = getNewDatasetInfo();
  const datasetId = Number(datasetInfo?.dataset_id || createdDataset.value?.id || 0);
  const dataset = store.state?.datasets?.datasetList?.find((entry) => entry.id === datasetId) || createdDataset.value;

  return {
    import_type: 'darknet',
    dataset_id: datasetId,
    dataset_name: dataset?.name || '',
    project_id: Number(datasetInfo?.project_id || createdDataset.value?.project_id || newDataset?.project_id || store.state.projects.currentProject?.id || 0),
    annotation_set_id: datasetInfo?.annotation_set_id || createdDataset.value?.annotation_sets?.[0]?.id || null,
    tag_name: datasetInfo?.tag_name || '',
    train_group_name: datasetInfo?.train_group_name || '',
    val_group_name: datasetInfo?.val_group_name || '',
    create_image_groups: shouldCreateImageGroups.value ? imageGroupsToCreate.value : undefined,
  };
}

function isCreatingNewDataset() {
  return Object.prototype.hasOwnProperty.call(destinationParams.value || {}, 'new_dataset');
}

function getExistingDatasetInfo() {
  return destinationParams.value?.new_dataset__on_false?.dataset_info || null;
}

function getNewDatasetInfo() {
  return destinationParams.value?.new_dataset || {};
}

function getDatasetRoute(projectId, datasetId) {
  return router.resolve({
    name: 'datasets.manager',
    params: {
      projectId: encodeID(projectId, 'p'),
    },
    hash: `#dataset-${datasetId}`,
  });
}

function buildDatasetToastMessage(datasetId) {
  const datasetUid = encodeID(datasetId, 'ds');
  return `Importing Darknet Dataset to ${datasetUid}`;
}

function navigateToDatasetCard(projectId, datasetId) {
  if (!projectId || !datasetId) {
    return;
  }

  const route = getDatasetRoute(projectId, datasetId);
  router.push(route);
}

async function createDatasetForImport() {
  const newDataset = getNewDatasetInfo();
  const response = await dataConnect.createDataset({
    name: newDataset.name,
    description: newDataset.description,
    annotation_set_name: newDataset.annotation_set_name || 'annotations',
    project_id: newDataset.project_id,
  });

  if (response?.error || !response?.result) {
    throw new Error(response?.error?.message || response?.error || 'Failed to create dataset');
  }

  createdDataset.value = response.result;

  return response.result;
}

async function createImportTask(importParams) {
  const appName = props.appDetails?.name || 'darknet-importer';
  const response = await dataConnect.addTask({
    name: `Importing dataset to ${importParams.dataset_name}`,
    app_name: appName,
    project_id: importParams.project_id,
    type: 'importer',
    data: {
      dataset_id: importParams.dataset_id,
      annotation_set_id: importParams.annotation_set_id,
      s3_path: importParams.importer_source,
      import_type: importParams.import_type,
      stage_definitions: [
        { importing: 'Importing files' },
      ],
    },
  });

  if (response?.error || !response?.result) {
    throw new Error(response?.error?.message || response?.error || 'Failed to create import task in database');
  }

  return response.result;
}

async function s3MultipleFileUpload({ fileList, importParams, ignoreDuplicate = false }) {
  const keys = [];
  for (const file of fileList) {
    const relativePath = file.relativePath || file.webkitRelativePath;
    const trimmedPath = relativePath ? relativePath.split('/').slice(1) : [file.name];
    keys.push(trimmedPath.join('/'));
  }

  const presignedResponse = await dataConnect.s3CreatePresignedUrl({
    dataset_id: importParams.dataset_id,
    ignore_duplicate: ignoreDuplicate,
    keys,
  });

  if (presignedResponse?.error !== undefined) {
    throw presignedResponse.error;
  }

  const presignedResult = presignedResponse?.result || {};

  if (presignedResult.duplicate_count > 0 && !ignoreDuplicate) {
    throw new Error(`Destination dataset contains ${presignedResult.duplicate_count} duplicate file${presignedResult.duplicate_count === 1 ? '' : 's'}.`);
  }

  const newFileList = [];
  if (presignedResult) {
    const {
      importerSource, indexes = [], presignedUrls = [], tnPresignedUrls = [],
    } = presignedResult;

    if (importerSource) {
      importParams.importer_source = importerSource;
    }

    presignedUrls.forEach((url, index) => {
      newFileList.push({
        url,
        thumbnail_url: tnPresignedUrls[index],
        file: fileList[indexes[index]],
      });
    });
  }

  // Created after the presign call so the task data holds s3_path for resuming.
  const task = await createImportTask(importParams);
  importParams.task = task;
  await dataConnect.taskAppParams({
    task_id: task.id,
    key: 'dataset_id',
    value: String(importParams.dataset_id),
  });
  await dataConnect.updateTaskStage({
    docker_task_id: task.id,
    stage: 'importing',
    status: 'uploading',
    percentage: 0,
    message: '',
  });

  try {
    return await importerStore.importByType({
      urls: newFileList,
      importParams,
    });
  } catch (error) {
    await dataConnect.updateTaskStage({
      docker_task_id: task.id,
      stage: 'importing',
      status: 'error',
      percentage: 100,
      message: `Error: ${error?.message || error}`,
    });
    throw error;
  }
}

function validateRequiredFields() {
  const missingFields = [];
  if (!fileObjs.value.length) {
    missingFields.push(sourceUploadField.value?.label || 'Upload Dataset Files');
  }
  if (schemaGroupRef.value) {
    missingFields.push(...schemaGroupRef.value.validateRequiredFields());
  }
  return missingFields;
}

async function start() {
  const missingFields = validateRequiredFields();
  if (missingFields.length > 0) {
    const uniqueMissingFields = Array.from(new Set(missingFields));
    throw new Error(`Required field${uniqueMissingFields.length > 1 ? 's' : ''} missing: ${uniqueMissingFields.join(', ')}`);
  }

  if (isCreatingNewDataset()) {
    await createDatasetForImport();
  }

  const importParams = buildImportParams();
  if (!importParams.dataset_id) {
    throw new Error('A destination dataset is required.');
  }

  const importPromise = s3MultipleFileUpload({
    fileList: fileObjs.value,
    importParams,
  });

  $toast.open({
    queue: true,
    duration: 8000,
    dismissible: true,
    message: buildDatasetToastMessage(importParams.dataset_id),
    type: 'success',
    position: 'top',
  });
  emit('completed');
  navigateToDatasetCard(importParams.project_id, importParams.dataset_id);

  importPromise.catch((error) => {
    const errorMessage = error?.message || String(error || 'Darknet import failed');
    $toast.open({
      queue: true,
      duration: 0,
      message: errorMessage,
      type: 'error',
      dismissible: true,
      position: 'top',
    });
  });
}

defineExpose({
  start,
  validateRequiredFields,
});
</script>

<style lang="scss" scoped>
.darknet-importer {
  display: contents;
}

.darknet-importer__group {
  padding: 6px 18px 20px;
}

.darknet-importer__title {
  margin-bottom: 2px;
  text-align: left;
  @include themify() {
    color: themed('color-primary');
  }
}

.darknet-importer__divider {
  width: 100%;
  margin: 0 0 12px;
  border: 0;
  border-top: 1px solid var(--color-primary-200);
}

.field-group {
  margin-bottom: 16px;
}

.select,
.text-input {
  width: 100%;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 6px;
  padding: 10px 12px;
  background: #fff;
  box-sizing: border-box;
  min-height: 44px;
}

.text-input--textarea {
  resize: vertical;
}

.uploader {
  width: 100%;
}

.uploader__label {
  display: block;
  margin: 6px 0 4px;
}

.drop-zone {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  padding: 10px;
  border: 2px dashed #ccc;
  border-radius: 6px;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.drop-zone._highlight {
  border-color: #250e81;
  background-color: rgba(37, 14, 129, 0.06);
}

.drop-zone__hint {
  font-size: 0.8rem;
  color: #777;
}

.uploader__row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.uploader__toggle {
  width: 200px;
}

.uploader__status {
  color: rgb(2, 68, 115);
}

.file-list {
  overflow-y: auto;
  max-height: 200px;
}

.file-list__row {
  display: flex;
  align-items: center;
  gap: 10px;
}
</style>
